import React, { useState, useMemo, useEffect } from 'react';
import s from './s.module.less';
import AIChat from 'components/AIChat';
import ProcessBar from './components/ProcessBar';
import CreateProfile from './components/Steps/CreateProfile';
import SetupService from './components/Steps/SetupService';
import ListOnKlarityHealth from './components/Steps/ListOnKlarityHealth';
import Drawer from './components/Steps/CreateProfile/Drawer';
import useGetPPAOnboardingStep from 'hooks/useGetPPAOnboardingStep';
import { Spin } from 'antd';
import { EPPAStep } from 'types/ppaOnboarding';
import useLoadBasicData from 'hooks/useLoadBasicData';
import {
    configureAvailabilityStep,
    createStep,
    listOnKlarityStep,
    serviceStep,
} from './utils';
import ConfigureAvailability from './components/Steps/ConfigureAvailability';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import usePPAOnboardingReviewInfo from 'hooks/usePPAOnboardingReviewInfo';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { trackHeapEvent } from 'utils/analytics';
import { EAIChatPageId } from 'types/ai';

export default function PPAOnboardingStepsPage() {
    const navigate = useNavigate();
    const [loadBasicData] = useLoadBasicData();
    const [reviewData, loading, loadReviewData] = usePPAOnboardingReviewInfo();
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [showProgress, setShowProgress] = useState(false);
    const [stepData, stepLoading] = useGetPPAOnboardingStep();
    const [step, setStep] = useState<EPPAStep>(EPPAStep.STEP_1); //EPPAStep.STEP_13

    const [providerHomeInfo] = useProviderHomeInfo();

    const savedStep = stepData?.currentStep;

    useEffect(() => {
        if (savedStep) {
            if (savedStep === EPPAStep.STEP_17_1) {
                // 完成onboarding跳转埋点
                trackHeapEvent(
                    'WebApp - PPA Onboarding Steps - View - Complete Redirect',
                    {
                        fromStep: savedStep,
                        toPage: 'review',
                    },
                );
                navigate(PATH.PPA_ONBOARDING_REVIEW, { replace: true });
            } else if (
                [
                    EPPAStep.STEP_18,
                    EPPAStep.STEP_19,
                    EPPAStep.STEP_20,
                    EPPAStep.STEP_21,
                ].includes(savedStep)
            ) {
                trackHeapEvent(
                    'WebApp - PPA Onboarding Steps - View - Complete Redirect',
                    {
                        fromStep: savedStep,
                        toPage: 'finish',
                    },
                );
                navigate(PATH.PPA_ONBOARDING_ADVANCE_SETTING, { replace: true });
            } else {
                setStep(savedStep);
            }
        }
    }, [savedStep, navigate]);

    useEffect(() => {
        loadBasicData();
    }, [loadBasicData]);

    // 页面访问埋点
    useEffect(() => {
        trackHeapEvent('WebApp - PPA Onboarding Steps - View - Page', {
            currentStep: step,
            hasSavedStep: !!savedStep,
            showProgress,
        });
    }, [step, savedStep, showProgress]);

    // 步骤变化埋点
    useEffect(() => {
        if (step) {
            trackHeapEvent(
                'WebApp - PPA Onboarding Steps - View - Step Change',
                {
                    step,
                },
            );
        }
    }, [step]);

    const commonProps = useMemo(() => {
        return {
            setShowProgress,
            step,
            setStep,
            refetchReviewData: loadReviewData,
            reviewData,
        };
    }, [step, reviewData, loadReviewData]);

    const mainStepNum = useMemo(() => {
        if (createStep.includes(step)) {
            return 1;
        }
        if (serviceStep.includes(step)) {
            return 2;
        }
        if (listOnKlarityStep.includes(step)) {
            return 3;
        }
        if (configureAvailabilityStep.includes(step)) {
            return 4;
        }

        return 1;
    }, [step]);

    const mainStepRender = useMemo(() => {
        if (!step) return <CreateProfile {...commonProps} />;
        if (mainStepNum === 1) {
            return <CreateProfile {...commonProps} />;
        }
        if (mainStepNum === 2) {
            return <SetupService {...commonProps} />;
        }
        if (mainStepNum === 3) {
            return <ListOnKlarityHealth {...commonProps} />;
        }
        if (mainStepNum === 4) {
            return <ConfigureAvailability {...commonProps} />;
        }

        return <ConfigureAvailability {...commonProps} />;
    }, [commonProps, step, mainStepNum]);

    const hasLogin = !!providerHomeInfo;

    const getAIChatPageId = useMemo((): EAIChatPageId => {
        if (!step) {
            return EAIChatPageId.PROVIDER_ONBOARDING_PROFILE_INTRO;
        }
        switch (step) {
            // Create Profile 步骤组
            case EPPAStep.STEP_1:
                return EAIChatPageId.PROVIDER_ONBOARDING_PROFILE_INTRO;
            case EPPAStep.STEP_2:
                return EAIChatPageId.PROVIDER_ONBOARDING_PROFILE_NAME;
            case EPPAStep.STEP_3:
                return EAIChatPageId.PROVIDER_ONBOARDING_PROFILE_CREDENTIALS;
            case EPPAStep.STEP_4:
                return EAIChatPageId.PROVIDER_ONBOARDING_PROFILE_GENDER;
            // Setup Service 步骤组
            case EPPAStep.STEP_5:
                return EAIChatPageId.PROVIDER_ONBOARDING_SERVICE_INTRO;
            case EPPAStep.STEP_6:
                return EAIChatPageId.PROVIDER_ONBOARDING_SERVICE_SPECIALTY;
            case EPPAStep.STEP_7:
                return EAIChatPageId.PROVIDER_ONBOARDING_SERVICE_PAYMENT_METHOD;
            case EPPAStep.STEP_8:
                return EAIChatPageId.PROVIDER_ONBOARDING_SERVICE_TEMPLATE;
            // List on Klarity Health 步骤组
            case EPPAStep.STEP_9:
                return EAIChatPageId.PROVIDER_ONBOARDING_KLARITY_INTRO;
            case EPPAStep.STEP_10:
                return EAIChatPageId.PROVIDER_ONBOARDING_KLARITY_SHOWCASE;
            case EPPAStep.STEP_11:
                return EAIChatPageId.PROVIDER_ONBOARDING_KLARITY_PERSONAL_ID;
            case EPPAStep.STEP_12:
                return EAIChatPageId.PROVIDER_ONBOARDING_KLARITY_MEDICAL_LICENSE;
            case EPPAStep.STEP_13:
                return EAIChatPageId.PROVIDER_ONBOARDING_KLARITY_PAYMENT_INFO;
            // Configure Availability 步骤组
            case EPPAStep.STEP_14:
                return EAIChatPageId.PROVIDER_ONBOARDING_AVAILABILITY_INTRO;
            case EPPAStep.STEP_15:
                return EAIChatPageId.PROVIDER_ONBOARDING_AVAILABILITY_WORKING_HOURS;
            case EPPAStep.STEP_16:
                return EAIChatPageId.PROVIDER_ONBOARDING_AVAILABILITY_ADVANCE_NOTICE;
            case EPPAStep.STEP_17:
                return EAIChatPageId.PROVIDER_ONBOARDING_AVAILABILITY_CONSENT_FORMS;
            case EPPAStep.STEP_17_1:
                return EAIChatPageId.PROVIDER_ONBOARDING_AVAILABILITY_CONSENT_FORMS_CONFIRM;
            default:
                return EAIChatPageId.PROVIDER_ONBOARDING_PROFILE_INTRO;
        }
    }, [step]);

    if (stepLoading || loading) {
        return <Spin spinning />;
    }

    // 抽屉显示/隐藏埋点
    const handleDrawerClose = () => {
        trackHeapEvent('WebApp - PPA Onboarding Steps - Click - Drawer Close', {
            currentStep: step,
            mainStepNum,
        });
        setDrawerVisible(false);
    };

    return (
        <div className={s.wrap}>
            <Drawer visible={drawerVisible} onClose={handleDrawerClose} />
            <PPAOnboardingHeader showLogout={hasLogin} />
            <div className={s.content}>
                <div className={s.contentIn}>
                    <div className={s.box}>
                        <div className={s.proceeBarWrap}>
                            {showProgress && (
                                <ProcessBar currentStep={mainStepNum} />
                            )}
                        </div>
                        {mainStepRender}
                    </div>
                    <AIChat pageId={getAIChatPageId} />
                </div>
            </div>
        </div>
    );
}
